<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card"
    />

    <!-- 検索エリア -->
    <div
      v-if="!isLoading"
      class="d-flex align-center mb-4"
    >
      <v-text-field
        v-model="searchQuery"
        :prepend-inner-icon="icons.mdiMagnify"
        label="名前・会員番号で検索"
        single-line
        hide-details
        clearable
        dense
        outlined
        style="max-width: 300px;"
        class="mr-2"
        @keyup.enter="handleSearch"
        @click:clear="handleClearSearch"
      />
      <v-btn
        color="secondary"
        :loading="isSearching"
        @click="handleSearch"
      >
        検索
      </v-btn>
    </div>

    <v-card v-if="!isLoading">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>顧客一覧</span>
        <div>
          <v-btn
            color="secondary"
            outlined
            class="mr-2"
            @click="openAddPropertyDialog"
          >
            <v-icon left>
              {{ icons.mdiPlaylistPlus }}
            </v-icon>
            プロパティを追加
          </v-btn>
          <v-btn
            color="primary"
            @click="openNewCustomerDialog"
          >
            <v-icon left>
              {{ icons.mdiPlus }}
            </v-icon>
            顧客を追加
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <CustomerList
          :customers="customers"
          :custom-fields="customFields"
          :total-count="pagination.totalCount"
          :page="pagination.page"
          :per-page="pagination.perPage"
          :loading="isSearching"
          @click="goToCustomerDetail"
          @update:pagination="handlePaginationChange"
        />
      </v-card-text>
    </v-card>

    <!-- 新規顧客追加ダイアログ -->
    <v-dialog
      v-if="newCustomerDialog"
      v-model="newCustomerDialog"
      width="500"
      persistent
    >
      <CustomerForm
        :type="'newUser'"
        @submit="handleCustomerCreate"
        @cancel="newCustomerDialog = false"
      />
    </v-dialog>

    <!-- プロパティ追加/編集ダイアログ -->
    <CustomerPropertyFormDialog
      v-model="propertyFormDialog"
      :property="editingProperty"
      @submit="handleAddProperty"
      @update="handleUpdateProperty"
    />

    <!-- プロパティ削除確認ダイアログ -->
    <CustomerPropertyDeleteConfirm
      v-model="deleteConfirmDialog"
      :property="deletingProperty"
      @confirm="handleDeleteProperty"
    />
  </div>
</template>

<script>
import {
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api'
import { mdiPlus, mdiMagnify, mdiPlaylistPlus } from '@mdi/js'
import useCompInit from '@/views/composable/useCompInit'
import useCustomers from '@/views/v2-temp/common/customer-management/composables/useCustomers'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import CustomerForm from '@/views/v2-temp/common/customer-management/components/CustomerForm.vue'
import CustomerList from '@/views/v2-temp/common/customer-management/components/CustomerList.vue'
import CustomerPropertyFormDialog from '@/views/v2-temp/common/customer-management/components/CustomerPropertyFormDialog.vue'
import CustomerPropertyDeleteConfirm from '@/views/v2-temp/common/customer-management/components/CustomerPropertyDeleteConfirm.vue'

export default {
  name: 'WaiterCustomerManagement',
  components: {
    CustomerForm,
    CustomerList,
    CustomerPropertyFormDialog,
    CustomerPropertyDeleteConfirm,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { initWith, isLoading } = useCompInit()

    const {
      customers,
      newCustomerDialog,
      searchQuery,
      isSearching,
      pagination,
      getCustomers,
      searchCustomers,
      createCustomer,
      openNewCustomerDialog,
    } = useCustomers(vm)

    const {
      customFields,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      getCustomFields,
      addCustomField,
      updateCustomField,
      deleteCustomField,
      openAddPropertyDialog,
      openEditPropertyDialog,
      confirmDeleteProperty,
    } = useCustomerCustomFields(vm)

    const handleCustomerCreate = async event => {
      const success = await createCustomer(event)
      if (success) {
        newCustomerDialog.value = false
      }
    }

    const handleSearch = () => {
      searchCustomers(searchQuery.value, { page: 1, perPage: pagination.value.perPage })
    }

    const handleClearSearch = () => {
      searchQuery.value = ''
      getCustomers({ page: 1, perPage: pagination.value.perPage })
    }

    const handlePaginationChange = ({ page, perPage }) => {
      if (searchQuery.value) {
        searchCustomers(searchQuery.value, { page, perPage })
      } else {
        getCustomers({ page, perPage })
      }
    }

    const goToCustomerDetail = customer => {
      vm.$router.push({
        name: 'waiter-settings-customer-management-show',
        params: {
          club_slug: vm.$route.params.club_slug,
          customerId: customer.id,
        },
      })
    }

    const handleAddProperty = event => {
      addCustomField(event)
    }

    const handleUpdateProperty = event => {
      updateCustomField(event.id, event)
    }

    const handleDeleteProperty = event => {
      deleteCustomField(deletingProperty.value.id, event)
    }

    onMounted(() => {
      initWith([
        getCustomers(),
        getCustomFields(),
      ])
    })

    return {
      isLoading,
      isSearching,
      customers,
      newCustomerDialog,
      searchQuery,
      pagination,
      customFields,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      openNewCustomerDialog,
      openAddPropertyDialog,
      handleCustomerCreate,
      handleAddProperty,
      handleUpdateProperty,
      handleDeleteProperty,
      handleSearch,
      handleClearSearch,
      handlePaginationChange,
      goToCustomerDetail,
      icons: {
        mdiPlus,
        mdiMagnify,
        mdiPlaylistPlus,
      },
    }
  },
}
</script>
